import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Modal from "../components/ui/Modal";
import CampaignForm, {
  EMPTY_CAMPAIGN,
  INTERVIEW_TYPE_OPTIONS,
  STATUS_OPTIONS,
  campaignToForm,
  buildCampaignPayload,
  validateCampaignForm,
} from "../components/CampaignForm";
import {
  listPlacementSessions,
  listCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  listInterviewTypes,
  listReferenceCompanies,
} from "../api/client";

// Interview Campaigns = reusable interview definitions. Each belongs to one
// batch and fans out to per-student assignments (managed on the detail page).

const selectClasses =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const TYPE_LABEL = Object.fromEntries(
  INTERVIEW_TYPE_OPTIONS.map((t) => [t.value, t.label])
);

function typeLabel(value) {
  if (!value) return "—";
  return TYPE_LABEL[value] || String(value).replace(/_/g, " ");
}

function StatusPill({ status }) {
  const map = {
    draft: "bg-muted text-muted-foreground",
    active: "bg-success/15 text-success",
    closed: "bg-warning/15 text-warning",
    archived: "bg-muted text-muted-foreground",
  };
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize " +
        (map[status] || "bg-muted text-muted-foreground")
      }
    >
      {status || "draft"}
    </span>
  );
}

function Chip({ icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2 py-1 text-xs text-muted-foreground">
      <Icon name={icon} size={12} />
      {children}
    </span>
  );
}

function CampaignCard({ campaign, onManage, onEdit, onDelete }) {
  const subtitle = [campaign.role, campaign.company].filter(Boolean).join(" · ");
  const topicCount = Array.isArray(campaign.topics) ? campaign.topics.length : 0;

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-foreground">
            {campaign.name}
          </h3>
          <p className="mt-0.5 truncate text-sm text-muted-foreground">
            {subtitle || campaign.batchName || "—"}
          </p>
        </div>
        <StatusPill status={campaign.status} />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <Chip icon="Layers">{campaign.batchName || "—"}</Chip>
        <Chip icon="Briefcase">{typeLabel(campaign.interviewType)}</Chip>
        <Chip icon="Gauge">{campaign.difficulty || "Medium"}</Chip>
        <Chip icon="Clock">{campaign.duration ?? 30} min</Chip>
        {topicCount > 0 && (
          <Chip icon="Tag">
            {topicCount} topic{topicCount === 1 ? "" : "s"}
          </Chip>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-muted/50 p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Users" size={13} /> Assigned
          </div>
          <div className="mt-1 text-lg font-semibold text-foreground">
            {campaign.assignedCount ?? 0}
          </div>
        </div>
        <div className="rounded-lg bg-muted/50 p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="CheckCircle2" size={13} /> Completed
          </div>
          <div className="mt-1 text-lg font-semibold text-foreground">
            {campaign.completedCount ?? 0}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Button variant="outline" size="sm" iconName="Send" onClick={() => onManage(campaign)}>
          Manage
        </Button>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(campaign)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={`Edit ${campaign.name}`}
            title="Edit"
          >
            <Icon name="Pencil" size={16} />
          </button>
          <button
            onClick={() => onDelete(campaign)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            aria-label={`Delete ${campaign.name}`}
            title="Delete"
          >
            <Icon name="Trash2" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Campaigns() {
  const location = useLocation();
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Reference catalogs for the campaign form (fetched from the DB so a campaign's
  // type/company always map to a real engine flow).
  const [interviewTypes, setInterviewTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [referenceLoading, setReferenceLoading] = useState(true);

  const [batchFilter, setBatchFilter] = useState(
    () => new URLSearchParams(location.search).get("batch") || ""
  );
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Create / edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_CAMPAIGN);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete confirm
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const loadBatches = useCallback(async () => {
    try {
      const rows = await listPlacementSessions();
      setBatches(rows);
    } catch {
      /* the campaigns load surfaces its own error */
    }
  }, []);

  // Load the interview-type + company catalogs once; the form falls back to a
  // built-in type list if this fails, so a failure never blocks campaign work.
  const loadReference = useCallback(async () => {
    setReferenceLoading(true);
    try {
      const [types, cos] = await Promise.all([
        listInterviewTypes().catch(() => []),
        listReferenceCompanies().catch(() => []),
      ]);
      setInterviewTypes(Array.isArray(types) ? types : []);
      setCompanies(Array.isArray(cos) ? cos : []);
    } finally {
      setReferenceLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = {};
      if (batchFilter) params.placementSessionId = batchFilter;
      if (statusFilter) params.status = statusFilter;
      const rows = await listCampaigns(params);
      setCampaigns(rows);
    } catch (err) {
      setLoadError(err?.error || "Could not load campaigns.");
    } finally {
      setLoading(false);
    }
  }, [batchFilter, statusFilter]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    loadReference();
  }, [loadReference]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const hasBatches = batches.length > 0;

  const batchName = useMemo(() => {
    if (!batchFilter) return "";
    return batches.find((b) => b.id === batchFilter)?.name || "";
  }, [batchFilter, batches]);

  const visible = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((c) => {
      const hay = `${c.name || ""} ${c.role || ""} ${c.company || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [campaigns, debouncedSearch]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...EMPTY_CAMPAIGN,
      placementSessionId: batchFilter || batches[0]?.id || "",
    });
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (campaign) => {
    setEditingId(campaign.id);
    setForm(campaignToForm(campaign));
    setFormError("");
    setModalOpen(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const isEdit = !!editingId;
    const err = validateCampaignForm(form, { requireBatch: !isEdit });
    if (err) {
      setFormError(err);
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const payload = buildCampaignPayload(form, { isEdit });
      if (isEdit) {
        await updateCampaign(editingId, payload);
      } else {
        await createCampaign(payload);
      }
      setModalOpen(false);
      await load();
    } catch (e2) {
      setFormError(e2?.error || "Could not save the campaign.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteCampaign(toDelete.id);
      setToDelete(null);
      await load();
    } catch (err) {
      setDeleteError(err?.error || "Could not delete the campaign.");
    } finally {
      setDeleting(false);
    }
  };

  const clearBatchFilter = () => {
    setBatchFilter("");
    navigate("/institution/campaigns", { replace: true });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Interview Campaigns</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {batchName ? (
              <span className="inline-flex items-center gap-2">
                Showing <span className="font-medium text-foreground">{batchName}</span>
                <button onClick={clearBatchFilter} className="text-primary underline">
                  clear
                </button>
              </span>
            ) : (
              "Define an interview once, then assign it to a whole cohort."
            )}
          </p>
        </div>
        <Button iconName="Plus" onClick={openCreate} disabled={!hasBatches}>
          New campaign
        </Button>
      </div>

      {/* No batches yet → must create one first */}
      {!hasBatches && !loading && (
        <div className="flex items-center gap-3 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground">
          <Icon name="Info" size={16} className="text-warning" />
          <span>You need a batch before creating a campaign.</span>
          <button
            onClick={() => navigate("/institution/batches")}
            className="ml-auto font-medium text-primary underline"
          >
            Create a batch
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            <Icon name="Search" size={16} />
          </span>
          <input
            type="text"
            placeholder="Search by name, role or company"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
        <select
          value={batchFilter}
          onChange={(e) => setBatchFilter(e.target.value)}
          className={selectClasses + " sm:w-56"}
        >
          <option value="">All batches</option>
          {batches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={selectClasses + " sm:w-40"}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Load error */}
      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{loadError}</span>
          <button onClick={load} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-56 animate-pulse rounded-xl border border-border bg-muted/40"
            />
          ))}
        </div>
      ) : visible.length === 0 && !loadError ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon name="Send" size={24} />
          </span>
          <h3 className="mt-4 text-base font-semibold text-foreground">
            {campaigns.length === 0 ? "No campaigns yet" : "No matching campaigns"}
          </h3>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            {campaigns.length === 0
              ? "Create your first interview campaign, then assign it to students in a batch."
              : "Try a different search or clear your filters."}
          </p>
          {campaigns.length === 0 && hasBatches && (
            <Button className="mt-5" iconName="Plus" onClick={openCreate}>
              Create your first campaign
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onManage={(x) => navigate(`/institution/campaigns/${x.id}`)}
              onEdit={openEdit}
              onDelete={(x) => {
                setToDelete(x);
                setDeleteError("");
              }}
            />
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      <Modal
        open={modalOpen}
        onClose={() => (saving ? null : setModalOpen(false))}
        title={editingId ? "Edit campaign" : "New campaign"}
        description={
          editingId
            ? "Update this interview definition."
            : "Define the interview once — reuse it for the whole cohort."
        }
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} loading={saving} iconName="Check">
              {editingId ? "Save changes" : "Create campaign"}
            </Button>
          </>
        }
      >
        <form onSubmit={submit}>
          <CampaignForm
            form={form}
            onField={setField}
            batches={batches}
            lockBatch={!!editingId}
            error={formError}
            interviewTypes={interviewTypes}
            companies={companies}
            referenceLoading={referenceLoading}
          />
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={!!toDelete}
        onClose={() => (deleting ? null : setToDelete(null))}
        title="Delete campaign"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setToDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete} loading={deleting} iconName="Trash2">
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          Delete <span className="font-medium text-foreground">{toDelete?.name}</span>? This
          can&apos;t be undone.
        </p>
        {deleteError && (
          <p className="mt-3 flex items-start gap-1.5 text-sm text-destructive">
            <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" /> {deleteError}
          </p>
        )}
      </Modal>
    </div>
  );
}
